import { apiClient } from '@/lib/api-client';

/**
 * Ops simulation T12 — registers (tills) and register sessions. A session
 * runs from opening to closing on one register; every bill rung on this
 * device carries it, so the drawer count and the session report read the
 * same bills.
 */

export interface Register {
  id: string;
  location_id: string;
  name: string;
  is_active: boolean;
  open_session_id: string | null;
  open_session_number: string | null;
  open_session_opened_at: string | null;
  current_cashier_id: string | null;
  current_cashier_name: string | null;
}

export interface RegisterSession {
  id: string;
  location_id: string;
  location_name: string;
  register_id: string;
  register_name: string;
  session_number: string;
  session_date: string;
  status: 'OPEN' | 'CLOSED';
  opening_cash: number;
  opened_by: string;
  opened_by_name: string;
  opened_at: string;
  current_cashier_id: string | null;
  current_cashier_name: string | null;
  closed_at: string | null;
  expected_cash: number | null;
  counted_cash: number | null;
  difference: number | null;
  difference_reason: string | null;
}

export interface RegisterDrawerPreview {
  register_session_id: string;
  session_number: string;
  status: 'OPEN' | 'CLOSED';
  opening_cash: number;
  cash_sales: number;
  cash_refunds: number;
  cash_expenses: number;
  expected_cash: number;
  /** Above this difference (minor units) a manager must approve the closing. */
  allowed_difference: number;
}

export interface RegisterStaff {
  id: string;
  name: string;
  role: string;
}

export interface OpenSessionDto {
  register_id: string;
  opening_cash?: number;
  opening_denominations?: Record<string, number>;
}

export interface CloseSessionDto {
  denominations?: Record<string, number>;
  counted_cash?: number;
  expected_cash: number;
  difference_reason?: string;
  approval_token?: string;
  notes?: string;
}

export const registerService = {
  listRegisters(locationId: string) {
    return apiClient<Register[]>(`/registers?location_id=${encodeURIComponent(locationId)}`);
  },

  getSession(id: string) {
    return apiClient<RegisterSession>(`/register-sessions/${id}`);
  },

  openSession(dto: OpenSessionDto) {
    return apiClient<RegisterSession>('/register-sessions', { method: 'POST', body: dto });
  },

  listStaff(sessionId: string) {
    return apiClient<RegisterStaff[]>(`/register-sessions/${sessionId}/staff`);
  },

  /** The signed-in user takes over without a PIN; anyone else types theirs. */
  switchCashier(sessionId: string, userId: string, pin?: string) {
    return apiClient<RegisterSession>(`/register-sessions/${sessionId}/cashier`, {
      method: 'POST',
      body: { user_id: userId, ...(pin ? { pin } : {}) },
    });
  },

  preview(sessionId: string) {
    return apiClient<RegisterDrawerPreview>(`/register-sessions/${sessionId}/preview`);
  },

  closeSession(sessionId: string, dto: CloseSessionDto) {
    return apiClient<RegisterSession>(`/register-sessions/${sessionId}/close`, {
      method: 'POST',
      body: dto,
    });
  },
};
